import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';

import useBoardStore from '../../../store/boardStore';
import useUserStore from '../../../store/userStore';
import IdeaCardVotes from '../IdeaCardVotes';
import TextIdeaCard from '../TextIdeaCard';
import CardComments from '../comments/CardComments';
import CardDetailCommentsPopover from './CardDetailCommentsPopover';
import { resolveIdeaImageSrc } from '../../../utils/ideaImage';
import { useCachedImage } from '../../../hooks/useCachedImage';
import { getPublicAssetUrl } from '../../../utils/publicAsset';
import { API_BASE_URL } from '../../../services/apiClient';
import { getColumnNavigationIdeas, type RandomOrderRef } from '../../../utils/boardCardOrdering';
import type { RankBy } from '../../../types/boardCardOrdering';
import type { Idea } from '../../../interfaces/Idea';

type CardDetailDialogProps = {
  rankBy: RankBy;
  orderedBoardIdeas: Idea[];
  columnRandomOrderRef: RandomOrderRef;
};

const CardDetailDialog = ({
  rankBy,
  orderedBoardIdeas,
  columnRandomOrderRef,
}: CardDetailDialogProps) => {
  const theme = useTheme();
  const isSmallWindow = useMediaQuery(theme.breakpoints.down('sm'));

  const viewCard = useBoardStore((state) => state.viewCard);
  const setViewCard = useBoardStore((state) => state.setViewCard);
  const boardDetails = useBoardStore((state) => state.boardDetails);
  const ideas = useBoardStore((state) => state.ideas);
  const participants = useBoardStore((state) => state.participants);
  const currentUser = useUserStore((state) => state.getCurrentUser());
  const votingEnabled = boardDetails?.votingEnabled !== false;
  const hasVoting = boardDetails?.votingStyle !== 'none';
  const hideVoteTotals = boardDetails?.privateVotingEnabled === true && !boardDetails?.isOwner;
  const commentsEnabled = boardDetails?.commentsEnabled !== false;

  const visibleIdeas = ideas.filter((idea) => {
    if (boardDetails?.cardsRequireApproval !== true) {
      return true;
    }

    if (idea.approved !== false) {
      return true;
    }

    if (boardDetails.isOwner) {
      return true;
    }

    return Boolean(currentUser && idea.ownerId === currentUser.id);
  });
  const activeCard = viewCard
    ? visibleIdeas.find((idea) => idea.id === viewCard.id) ?? viewCard
    : null;
  const activeImageSrc =
    activeCard && (activeCard.type === 'image' || activeCard.type === 'video')
      ? resolveIdeaImageSrc(activeCard)
      : '';
  const activeVideoSrc =
    activeCard?.type === 'video'
      ? `${API_BASE_URL}/boardIdeas/video/${activeCard.topic}/${activeCard.id}`
      : '';
  const activeFileSrc =
    activeCard?.type === 'file'
      ? `${API_BASE_URL}/boardIdeas/file/${activeCard.topic}/${activeCard.id}`
      : '';
  const isPdfCard =
    activeCard?.type === 'file' &&
    (activeCard.mimeType === 'application/pdf' ||
      activeCard.title?.trim().toLowerCase().endsWith('.pdf') === true ||
      activeCard.content?.trim().toLowerCase().endsWith('.pdf') === true);
  const {
    isLoading: isImageLoading,
    handleLoad: handleImageLoad,
    handleError: handleImageError,
  } = useCachedImage(activeImageSrc);

  if (!activeCard) {
    return null;
  }

  const comments = activeCard.comments ?? [];
  const navigationIdeas =
    boardDetails?.layout === 'columns'
      ? getColumnNavigationIdeas({
          ideas: visibleIdeas,
          columns: boardDetails.columns ?? [],
          rankBy,
          randomizeCardPosition: boardDetails.randomizeCardPosition === true,
          randomOrderRef: columnRandomOrderRef,
        })
      : orderedBoardIdeas;
  const canNavigate = navigationIdeas.length > 1;
  const canShowVotes = Boolean(boardDetails) && hasVoting && (votingEnabled || !hideVoteTotals);

  const cycleCard = (offset: number) => {
    if (!viewCard || !canNavigate) {
      return;
    }

    const currentIndex = navigationIdeas.findIndex((idea) => idea.id === activeCard.id);
    if (currentIndex === -1) {
      setViewCard(navigationIdeas[0]);
      return;
    }

    const nextIndex = (currentIndex + offset + navigationIdeas.length) % navigationIdeas.length;
    setViewCard(navigationIdeas[nextIndex]);
  };

  return (
    <Dialog fullScreen open onClose={() => setViewCard(null)} PaperProps={{ m: 0 }}>
      <DialogContent
        sx={{
          flex: 1,
          p: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#222',
        }}
      >
        <Stack direction="row" sx={{ height: '100%', flex: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {activeCard.type === 'image' ? (
              <Card
                sx={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: '#222',
                  boxShadow: 'none',
                }}
                elevation={3}
              >
                <CardContent
                  sx={{
                    position: 'relative',
                    p: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    '&:last-child': { pb: 0 },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={activeImageSrc}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'contain',
                      opacity: isImageLoading ? 0 : 1,
                    }}
                  />
                  {isImageLoading && (
                    <CardMedia
                      component="img"
                      image={getPublicAssetUrl('/img/dotstorming-logo-compact.gif')}
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        height: '100%',
                        width: '100%',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            ) : activeCard.type === 'video' ? (
              <Card
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  p: 2,
                  overflow: 'auto',
                  backgroundColor: '#111',
                  maxHeight: '100%',
                  width: '100%',
                  maxWidth: 1000,
                }}
                elevation={3}
              >
                <CardContent
                  sx={{
                    p: 0,
                    width: '100%',
                    '&:last-child': { pb: 0 },
                  }}
                >
                  {activeCard.processingStatus === 'processing' ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ minHeight: 320 }}
                    >
                      <CardMedia
                        component="img"
                        image={getPublicAssetUrl('/img/dotstorming-logo-compact.gif')}
                        sx={{ width: 56, height: 56 }}
                      />
                    </Box>
                  ) : activeCard.processingStatus === 'errored' ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{ minHeight: 320, color: '#fff', textAlign: 'center' }}
                    >
                      <Typography variant="h6">Video processing failed</Typography>
                    </Box>
                  ) : (
                    <Box
                      component="video"
                      controls
                      poster={activeImageSrc || undefined}
                      src={activeVideoSrc}
                      sx={{
                        display: 'block',
                        width: '100%',
                        maxHeight: '70vh',
                        backgroundColor: '#000',
                      }}
                    />
                  )}
                  {!isSmallWindow && activeCard.title && activeCard.title.trim().length > 0 && (
                    <Typography
                      variant="subtitle1"
                      sx={{
                        mt: 2,
                        px: 1,
                        color: '#fff',
                      }}
                      dangerouslySetInnerHTML={{
                        __html: activeCard.title,
                      }}
                    />
                  )}
                </CardContent>
              </Card>
            ) : isPdfCard ? (
              <Card
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'stretch',
                  p: 0,
                  overflow: 'hidden',
                  m: '100px',
                  backgroundColor: '#fff',
                  width: 'calc(100% - 200px)',
                  height: '100%',
                  maxWidth: 'calc(100% - 200px)',
                  maxHeight: '100%',
                }}
                elevation={3}
              >
                <CardContent
                  sx={{
                    p: 0,
                    width: '100%',
                    height: '100%',
                    '&:last-child': { pb: 0 },
                  }}
                >
                  <Box
                    component="embed"
                    src={activeFileSrc}
                    type="application/pdf"
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  p: 2,
                  overflow: 'auto',
                  backgroundColor: '#fff',
                  maxHeight: '100%',
                  width: '100%',
                  maxWidth: 800,
                }}
                elevation={3}
              >
                <CardContent
                  sx={{
                    p: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    '&:last-child': { pb: 0 },
                  }}
                >
                  <TextIdeaCard idea={activeCard} showBackground={false} size="large" />
                </CardContent>
              </Card>
            )}
            {!isSmallWindow &&
              (activeCard.type === 'image' || isPdfCard) &&
              activeCard.title &&
              activeCard.title.trim().length > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    position: 'absolute',
                    right: 24,
                    bottom: 24,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: '#fff',
                    boxShadow: 1,
                    zIndex: 1,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: activeCard.title,
                  }}
                />
              )}
            {canShowVotes && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: isSmallWindow ? 16 : 24,
                  left: isSmallWindow ? 16 : 24,
                  backgroundColor: 'background.paper',
                  px: 1.5,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 4,
                  boxShadow: 1,
                }}
              >
                <IdeaCardVotes
                  idea={activeCard}
                  size="large"
                  layout="horizontal"
                  hideVoteOwners={true}
                />
              </Box>
            )}
            {commentsEnabled && isSmallWindow && (
              <CardDetailCommentsPopover
                ideaId={activeCard.id}
                comments={comments}
                participants={participants}
              />
            )}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
              }}
            >
              <IconButton
                aria-label="View previous card"
                onClick={() => cycleCard(-1)}
                disabled={!canNavigate}
                sx={{
                  boxShadow: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton
                aria-label="View next card"
                onClick={() => cycleCard(1)}
                disabled={!canNavigate}
                sx={{
                  boxShadow: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Stack>
            <IconButton
              aria-label="Close card view"
              onClick={() => setViewCard(null)}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                boxShadow: 1,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {commentsEnabled && !isSmallWindow && (
            <Box sx={{ position: 'relative', width: 320, backgroundColor: '#fff' }}>
              <Box sx={{ overflow: 'auto', height: '100%', pb: 2 }}>
                <CardComments
                  ideaId={activeCard.id}
                  comments={comments}
                  participants={participants}
                />
              </Box>
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CardDetailDialog;
